import { CTA } from "common/styleguide/CTA";
import { commonCTAs, Msg, useFormatMessage } from "lib/localization";
import { experimentEnabled } from "lib/utils";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import { messages as LeftSidebarMessages } from "../LeftSidebar";
import { RightSidebarTool } from "../SelectionToolHelper";
import { autotrace } from "./autotrace";
import styles from "./TraceTool.scss";

const ChooseTraceMethod = ({ dispatch }) => (
  <>
    <p>
      <T
        id="ChooseTraceMethod.autoTraceDescription"
        defaultMessage="Use Auto-Trace to automatically trace a route to get a cuesheet. This is an imperfect process and you will need to review the results for accuracy."
      />
    </p>
    <p>
      <T
        id="ChooseTraceMethod.manualTraceDescription"
        defaultMessage="Use Manual Trace to turn the current route into a gray line, which you can use as a reference to manually draw a new route over."
      />
    </p>
    <CTA onClick={() => dispatch("autoTrace")}>
      <T id="ChooseTraceMethod.autoTrace" defaultMessage="Auto-Trace" />
    </CTA>
    <CTA link onClick={() => dispatch("manualTrace")}>
      <T id="ChooseTraceMethod.manualTrace" defaultMessage="Manual Trace" />
    </CTA>
    {experimentEnabled("routing") && (
      <>
        <CTA secondary onClick={() => dispatch("traceCycling")}>
          Redraw (Cycling)
        </CTA>
        <CTA secondary onClick={() => dispatch("traceExperiment")}>
          Redraw (Experiment)
        </CTA>
        <CTA secondary onClick={() => dispatch("autoTraceExperiment")}>
          Auto-Trace (Experiment)
        </CTA>
      </>
    )}
  </>
);

const ManualTrace = ({ dispatch, manualTrace }) => {
  useEffect(() => {
    // Make sure we can undo the manual trace
    manualTrace();
  }, []);

  return (
    <>
      <T id="ManualTrace.confirm" defaultMessage="Your route is ready to trace." />
      <CTA onClick={() => dispatch("close")}>
        <Msg m={commonCTAs.ok} />
      </CTA>
      <CTA link onClick={() => dispatch("cancel")}>
        <Msg m={commonCTAs.cancel} />
      </CTA>
    </>
  );
};

const messages = defineMessages({
  traced: {
    id: "AutoTrace.traced",
    defaultMessage: "traced",
  },
  refiningSegment: {
    id: "AutoTrace.refiningSegment",
    defaultMessage: "Refining segment:",
  },
});

const AutoTrace = ({ dispatch, experiment }) => {
  const [status, setStatus] = useState();
  const fm = useFormatMessage();
  const stoppedRef = useRef();
  const routeRef = useRef();
  useEffect(() => {
    // Make sure we can undo the auto-trace
    window.Routes.activeMap.disableClicks();
    const lineColor = window.Routes.activeMap.lineColor;
    autotrace(window.Routes.activeMap.activeRoute, setStatus, stoppedRef, experiment, lineColor).then((res) => {
      if (res && !stoppedRef.current) {
        routeRef.current = new window.rwgps.Route(
          { track_points: res.trackPoints, course_points: res.coursePoints },
          window.Routes.activeMap
        );
        window.Routes.activeMap.activeRoute.pois.forEach((poi) => {
          routeRef.current.pois.push(poi.copy());
        });
        routeRef.current.name = `${
          window.Routes.activeMap.activeRoute.name || fm(LeftSidebarMessages.unnamedRoute)
        } (${fm(messages.traced)})`;
        window.Routes.activeMap.addRoute(routeRef.current);
        window.Routes.activeMap.snapshot({ doSync: true });
        window.Routes.activeMap.setActiveRoute(routeRef.current);
        // Spur correction
        let i = 0;
        const checkAllSpurs = () => {
          return new Promise((resolve, reject) => {
            const checkNextSpur = () => {
              setStatus(`${fm(messages.refiningSegment)} ${i}`);
              window.rwgps.checkSpur(routeRef.current.segments[i], true, () => {
                if (routeRef.current.segments[++i]) {
                  checkNextSpur();
                } else {
                  resolve();
                }
              });
            };
            checkNextSpur();
          });
        };
        checkAllSpurs().then(() => {
          // Fetch missing elevations, final metrics calc, and get it on the map
          window.Routes.activeMap.fetchElevations(routeRef.current.trackPoints(), () => {
            routeRef.current.calculateMetrics();
            window.Routes.activeMap.loadElevationProfile();
            window.Routes.activeMap.drawRouteDistanceMarkers(routeRef.current);
            window.Routes.activeMap.drawCoursePointIcons(routeRef.current.coursePoints());
            window.Routes.activeMap.populateCuesheet(routeRef.current.coursePoints());
            window.rwgps.Events.trigger("map:routes_changed");
            setStatus("success");
          });
        });
      } else {
        setStatus("error");
      }
    });
    return () => {
      window.Routes.activeMap.enableClicks();
    };
  }, []);

  return (
    <>
      {status !== "success" && status !== "error" && (
        <>
          <p>
            <T id="AutoTrace.progress" defaultMessage="Auto-Trace in progress. This should only take a moment." />
          </p>
          <p className={styles.status}>{status}</p>
          <CTA
            onClick={() => {
              stoppedRef.current = true;
              dispatch("close");
            }}
          >
            <Msg m={commonCTAs.cancel} />
          </CTA>
        </>
      )}
      {status === "error" && (
        <>
          <T
            id="AutoTrace.error"
            defaultMessage="We were unable to automatically trace your route. Please try manually tracing."
          />
          <CTA onClick={() => dispatch("close")}>
            <Msg m={commonCTAs.ok} />
          </CTA>
        </>
      )}
      {status === "success" && (
        <>
          <p style={{ fontWeight: 500 }}>
            <T id="AutoTrace.success" defaultMessage="Auto-Trace completed successfully." />
          </p>
          <p>
            <T
              id="AutoTrace.successDisclaimer"
              defaultMessage="Please carefully inspect the resulting route, it is not guaranteed to be free of errors."
            />
          </p>
          <CTA onClick={() => dispatch("close")}>
            <Msg m={commonCTAs.ok} />
          </CTA>
          <CTA link onClick={() => dispatch("cancel")}>
            <Msg m={commonCTAs.cancel} />
          </CTA>
        </>
      )}
    </>
  );
};

const Redraw = ({ dispatch, mode }) => {
  useEffect(() => {
    const map = window.Routes.activeMap;
    map.disableClicks();
    // temporarily disable spur correction
    const checkSpur = window.rwgps.checkSpur;
    window.rwgps.checkSpur = () => {};

    const clickOptions = new window.rwgps.ClickOptions();
    if (mode === "experiment") {
      clickOptions.cyclingExperimentMode(true);
    }
    const clicks = map.activeRoute.segments.map((seg) => ({
      point: seg.prevControlPoint.point,
      opts: clickOptions,
      color: seg.color,
      fetchEles: true,
    }));
    const lastSeg = map.activeRoute.lastSegment;

    clicks.push({
      point: lastSeg.nextControlPoint.point,
      opts: clickOptions,
      color: lastSeg.color,
    });

    const nextClick = () => {
      console.log("nextClick");
      if (clicks.length) {
        map.clicks.push(clicks.shift());
        map.dispatchNextClick();
      }
    };

    const route = new window.rwgps.Route(null, map);
    map.setActiveRoute(route);
    window.rwgps.Events.bind("map:routing_complete", nextClick);
    nextClick();
    nextClick();

    return () => {
      window.Routes.activeMap.enableClicks();
      window.rwgps.Events.unbind("map:routing_complete", nextClick);
      window.rwgps.checkSpur = checkSpur;
    };
  }, []);

  return (
    <>
      <p style={{ fontWeight: 500 }}>Redrawing route in {mode} mode.</p>
      <CTA onClick={() => dispatch("close")}>Close</CTA>
    </>
  );
};

const reducer = (state, action) => {
  switch (action) {
    case "manualTrace":
      return { component: ManualTrace };
    case "autoTrace":
      return { component: AutoTrace };
    case "autoTraceExperiment":
      return { component: AutoTrace, props: { experiment: true } };
    case "traceCycling":
      return { component: Redraw, props: { mode: "cycling" } };
    case "traceExperiment":
      return { component: Redraw, props: { mode: "experiment" } };
    case "cancel":
      return { effect: "cancel" };
    case "close":
      return { effect: "close" };
  }
};

export const TraceTool = ({ onRequestClose, ...props }) => {
  const [state, dispatch] = useReducer(reducer, {
    component: ChooseTraceMethod,
  });

  useEffect(() => {
    switch (state.effect) {
      case "close":
        onRequestClose();
        break;
      case "cancel":
        window.Routes.activeMap.undo();
        onRequestClose();
        break;
    }
  }, [state.effect]);

  return (
    <RightSidebarTool onRequestClose={onRequestClose} title={"Trace"}>
      <div className={styles.TraceTool}>
        {state.component && <state.component dispatch={dispatch} {...props} {...(state.props || {})} />}
      </div>
    </RightSidebarTool>
  );
};
